import { memo, useEffect, useState } from 'react';
import PlanetCanvas from './planet/PlanetCanvas';
import { devApi } from '../api/dev';
import { setDevUserId, setGameSession } from '../api/client';
import { authApi } from '../api/auth';
import { Globe, Leaf, Fish, Building2, Microscope, Rocket, ChevronRight, Play, Orbit, Clock, User, KeyRound, Lock } from 'lucide-react';

export const BRAND_NAME = 'PlanetSeed';
export const BRAND_LOGO_SRC = '/favicon.svg';
export const BETA_LABEL = 'Beta preview';

export const LANDING_PLANET_STAGE = 8;
export const LANDING_PLANET_VISUAL_STATE = {
  waterOpacity: 0.62,
  atmosphereOpacity: 0.5,
  vegetationOpacity: 1,
  civilizationOpacity: 1,
  technologyGlow: 1,
  starColor: 0xffc87a,
  waterColor: 0x145f85,
  atmosphereColor: 0xc8d8ff,
};
export const LANDING_ORBIT_EPOCH_MS = Date.UTC(2025, 0, 1);
export const LANDING_SURFACE_ITEMS = [
  { item_id: 'showcase-coastal-land', angle: 350, altitude: 0.04, layer: 'surface', scale: 1.12, upgrade: { category: 'geology', name: 'Coastal Land' } },
  { item_id: 'showcase-continent-north', angle: 62, altitude: 0.02, layer: 'surface', scale: 1.2, upgrade: { category: 'geology', name: 'Continent' } },
  { item_id: 'showcase-continent-east', angle: 142, altitude: -0.06, layer: 'surface', scale: 1.14, upgrade: { category: 'geology', name: 'Continent' } },
  { item_id: 'showcase-continent-west', angle: 198, altitude: 0.0, layer: 'surface', scale: 1.1, upgrade: { category: 'geology', name: 'Continent' } },
  { item_id: 'showcase-continent-south', angle: 232, altitude: 0.08, layer: 'surface', scale: 1.16, upgrade: { category: 'civilisation', name: 'Land Reclamation' } },
  { item_id: 'showcase-ocean', angle: 286, altitude: -0.08, layer: 'surface', scale: 1.05, upgrade: { category: 'water', name: 'Ocean Patch' } },
  { item_id: 'showcase-forest-1', angle: 45, altitude: 0.2, layer: 'surface', scale: 1.15, upgrade: { category: 'plant', name: 'Forest Patch' } },
  { item_id: 'showcase-forest-2', angle: 155, altitude: -0.2, layer: 'surface', scale: 1.05, upgrade: { category: 'plant', name: 'Simple Plants' } },
  { item_id: 'showcase-biodiversity', angle: 312, altitude: 0.16, layer: 'surface', scale: 1.06, upgrade: { category: 'life', name: 'Habitat Diversity' } },
  { item_id: 'showcase-tribe', angle: 18, altitude: 0.2, layer: 'surface', scale: 1.05, upgrade: { category: 'civilisation', name: 'Early Tribe' } },
  { item_id: 'showcase-settlement', angle: 42, altitude: 0.22, layer: 'surface', scale: 1.12, upgrade: { category: 'civilisation', name: 'Early Settlement' } },
  { item_id: 'showcase-migration', angle: 96, altitude: -0.14, layer: 'surface', scale: 1.0, upgrade: { category: 'civilisation', name: 'Migration' } },
  { item_id: 'showcase-trading', angle: 126, altitude: 0.12, layer: 'surface', scale: 1.08, upgrade: { category: 'civilisation', name: 'Settlement Trading' } },
  { item_id: 'showcase-farm', angle: 78, altitude: -0.08, layer: 'surface', scale: 1.0, upgrade: { category: 'civilisation', name: 'Farm' } },
  { item_id: 'showcase-village', angle: 134, altitude: 0.18, layer: 'surface', scale: 1.12, upgrade: { category: 'civilisation', name: 'Village' } },
  { item_id: 'showcase-town', angle: 206, altitude: -0.16, layer: 'surface', scale: 1.14, upgrade: { category: 'civilisation', name: 'Town' } },
  { item_id: 'showcase-school', angle: 178, altitude: 0.12, layer: 'surface', scale: 0.98, upgrade: { category: 'civilisation', name: 'School' } },
  { item_id: 'showcase-water-system', angle: 262, altitude: -0.1, layer: 'surface', scale: 1.0, upgrade: { category: 'civilisation', name: 'Water System' } },
  { item_id: 'showcase-city', angle: 292, altitude: 0.1, layer: 'surface', scale: 1.18, upgrade: { category: 'civilisation', name: 'City' } },
  { item_id: 'showcase-hospital', angle: 322, altitude: -0.18, layer: 'surface', scale: 1.02, upgrade: { category: 'civilisation', name: 'Hospital' } },
  { item_id: 'showcase-road', angle: 330, altitude: -0.12, layer: 'surface', scale: 1.0, upgrade: { category: 'civilisation', name: 'Road Network' } },
  { item_id: 'showcase-factory', angle: 244, altitude: 0.16, layer: 'surface', scale: 1.05, upgrade: { category: 'civilisation', name: 'Factory' } },
  { item_id: 'showcase-power', angle: 316, altitude: -0.24, layer: 'surface', scale: 0.95, upgrade: { category: 'civilisation', name: 'Power Plant' } },
  { item_id: 'showcase-clean-energy', angle: 20, altitude: -0.18, layer: 'surface', scale: 1.0, upgrade: { category: 'civilisation', name: 'Clean Energy' } },
  { item_id: 'showcase-research', angle: 118, altitude: 0.24, layer: 'surface', scale: 1.04, upgrade: { category: 'technology', name: 'Research Centre' } },
  { item_id: 'showcase-radio', angle: 104, altitude: 0.25, layer: 'surface', scale: 1.0, upgrade: { category: 'technology', name: 'Radio Tower' } },
  { item_id: 'showcase-star-study', angle: 128, altitude: 0.22, layer: 'surface', scale: 0.95, upgrade: { category: 'technology', name: 'Study Parent Star' } },
  { item_id: 'showcase-telescope', angle: 188, altitude: 0.26, layer: 'surface', scale: 1.0, upgrade: { category: 'technology', name: 'Telescope' } },
  { item_id: 'showcase-signal-array', angle: 216, altitude: 0.24, layer: 'surface', scale: 0.98, upgrade: { category: 'technology', name: 'Signal Array' } },
  { item_id: 'showcase-launch-complex', angle: 252, altitude: 0.21, layer: 'surface', scale: 1.04, upgrade: { category: 'technology', name: 'Launch Complex' } },
  { item_id: 'showcase-space-port', angle: 344, altitude: 0.18, layer: 'surface', scale: 1.08, upgrade: { category: 'technology', name: 'Space Port' } },
  { item_id: 'showcase-rocket-program', angle: 14, altitude: 0.24, layer: 'surface', scale: 1.1, upgrade: { category: 'technology', name: 'Rocket Program' } },
  { item_id: 'showcase-defence-grid', angle: 276, altitude: 0.24, layer: 'surface', scale: 0.98, upgrade: { category: 'technology', name: 'Planetary Defence Grid' } },
  { item_id: 'showcase-satellite', angle: 20, altitude: 0.45, layer: 'orbit', scale: 1.0, upgrade: { category: 'technology', name: 'Satellite' } },
  { item_id: 'showcase-orbital-telescope', angle: 88, altitude: 0.52, layer: 'orbit', scale: 1.02, upgrade: { category: 'technology', name: 'Orbital Telescope' } },
  { item_id: 'showcase-orbital-dock', angle: 168, altitude: 0.56, layer: 'orbit', scale: 1.05, upgrade: { category: 'technology', name: 'Orbital Dock' } },
  { item_id: 'showcase-deep-space-probe', angle: 246, altitude: 0.58, layer: 'orbit', scale: 0.96, upgrade: { category: 'technology', name: 'Deep Space Probe' } },
  { item_id: 'showcase-orbital-defence', angle: 302, altitude: 0.5, layer: 'orbit', scale: 1.0, upgrade: { category: 'technology', name: 'Orbital Defence Platform' } },
  { item_id: 'showcase-shield-satellites', angle: 334, altitude: 0.54, layer: 'orbit', scale: 1.0, upgrade: { category: 'technology', name: 'High-Orbit Shield Satellites' } },
];

interface DevPlanet {
  planet_id: string;
  owner_user_id: string;
  name: string;
  stage: number;
  user?: { name?: string | null };
  _count?: { upgrade_instances?: number };
}

export const LANDING_FEATURES = [
  { icon: Globe, label: 'Terraform', detail: 'Climate', accent: 'text-cyan-300 bg-cyan-400/10 border-cyan-300/20' },
  { icon: Leaf, label: 'Seed life', detail: 'Biomes', accent: 'text-emerald-300 bg-emerald-400/10 border-emerald-300/20' },
  { icon: Fish, label: 'Evolve', detail: 'Species', accent: 'text-violet-300 bg-violet-400/10 border-violet-300/20' },
  { icon: Building2, label: 'Civilise', detail: 'Cities', accent: 'text-amber-300 bg-amber-400/10 border-amber-300/20' },
  { icon: Microscope, label: 'Research', detail: 'Tech tree', accent: 'text-sky-300 bg-sky-400/10 border-sky-300/20' },
  { icon: Rocket, label: 'Conquer space', detail: 'Fleets · pirates', accent: 'text-rose-300 bg-rose-400/10 border-rose-300/20' },
];

function BrandMark({ className = '', imageClassName = '' }: { className?: string; imageClassName?: string }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden border border-cyan-200/20 bg-slate-900/75 shadow-lg shadow-cyan-950/30 ${className}`}
      aria-hidden="true"
    >
      <img src={BRAND_LOGO_SRC} alt="" className={`h-full w-full object-contain ${imageClassName}`} />
    </span>
  );
}

function BrandLockup({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <BrandMark className={compact ? 'h-8 w-8 rounded-lg p-0.5' : 'h-11 w-11 rounded-xl p-1'} />
      <div className="min-w-0">
        <div className={`${compact ? 'text-sm' : 'text-base'} font-semibold tracking-wide text-white`}>
          {BRAND_NAME}
        </div>
        {!compact && (
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-200/70">
            Worlds grown by learning
          </div>
        )}
      </div>
    </div>
  );
}

export const LandingPlanetBackdrop = memo(function LandingPlanetBackdrop() {
  return (
    <PlanetCanvas
      stage={LANDING_PLANET_STAGE}
      visualState={LANDING_PLANET_VISUAL_STATE}
      surfaceItems={LANDING_SURFACE_ITEMS}
      rotationSpeed={0.00016}
      planetId="landing-showcase"
      orbitEpochMs={LANDING_ORBIT_EPOCH_MS}
      dayDurationMs={1800000}
    />
  );
});

export default function LandingPage() {
  const [entryRole, setEntryRole] = useState<'student' | 'teacher'>('student');
  const [studentMode, setStudentMode] = useState<'join' | 'login'>('join');
  const [studentUniverseMode, setStudentUniverseMode] = useState<'public' | 'teacherCode'>('public');
  const [teacherMode, setTeacherMode] = useState<'login' | 'register'>('login');
  const [studentUsername, setStudentUsername] = useState('');
  const [studentUniverseCode, setStudentUniverseCode] = useState('');
  const [studentPassword, setStudentPassword] = useState('');
  const [teacherName, setTeacherName] = useState('');
  const [teacherEmail, setTeacherEmail] = useState('');
  const [teacherPassword, setTeacherPassword] = useState('');
  const [devTeacherName, setDevTeacherName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFeatures, setShowFeatures] = useState(false);
  const [planets, setPlanets] = useState<DevPlanet[]>([]);
  const [showContinue, setShowContinue] = useState(false);
  const isDev = import.meta.env.DEV;

  useEffect(() => {
    const t = window.setTimeout(() => setShowFeatures(true), 800);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!isDev) return;
    devApi.getPlanets().then((res) => {
      if (res.success && res.data) {
        setPlanets(res.data as DevPlanet[]);
      }
    }).catch(() => {
      // The dev endpoint may not be available while the backend is booting.
    });
  }, [isDev]);

  const authError = (err: unknown, fallback: string) => {
    const error = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
    return error.response?.data?.error?.message || error.message || fallback;
  };

  const handleContinue = (userId: string) => {
    setDevUserId(userId);
    window.location.reload();
  };

  const handleStudentAuth = async () => {
    if (!studentUsername.trim() || (studentUniverseMode === 'teacherCode' && !studentUniverseCode.trim()) || studentPassword.length < 6) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        username: studentUsername.trim(),
        universeCode: studentUniverseMode === 'teacherCode' ? studentUniverseCode.trim() : undefined,
        password: studentPassword,
        joinMode: studentUniverseMode,
      };
      const result = studentMode === 'join'
        ? await authApi.studentJoin(payload)
        : await authApi.studentLogin(payload);
      if (!result.data?.session) {
        setError('Student login returned no planet session');
        return;
      }
      setGameSession(result.data.session);
      window.location.href = '/';
    } catch (err: unknown) {
      setError(authError(err, studentMode === 'join' ? 'Could not join this universe' : 'Could not return to this planet'));
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherAuth = async () => {
    if (!teacherEmail.trim() || teacherPassword.length < 6) return;
    setLoading(true);
    setError(null);
    try {
      const result = teacherMode === 'register'
        ? await authApi.teacherRegister({ email: teacherEmail.trim(), password: teacherPassword, name: teacherName.trim() || undefined })
        : await authApi.teacherLogin({ email: teacherEmail.trim(), password: teacherPassword });
      if (!result.data?.session) {
        setError('Teacher login returned no session');
        return;
      }
      setGameSession(result.data.session);
      window.location.href = '/teacher';
    } catch (err: unknown) {
      setError(authError(err, teacherMode === 'register' ? 'Could not create teacher account' : 'Could not log in as teacher'));
    } finally {
      setLoading(false);
    }
  };

  const handleDevTeacherCreate = async () => {
    if (!devTeacherName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await devApi.seedTeacher(devTeacherName.trim());
      if (result.data?.teacher) {
        setDevUserId(result.data.teacher.user_id);
        window.location.href = '/teacher';
      } else {
        setError('Teacher creation returned no data');
      }
    } catch (err: unknown) {
      const msg = authError(err, 'Network error - is the server running?');
      setError(msg.includes('Database is unavailable') ? msg : `Failed to create dev teacher: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-slate-950">
      <div className="absolute inset-0 md:right-[30rem]">
        <LandingPlanetBackdrop />
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/20 to-slate-950/80 pointer-events-none" />
      <div className="hidden md:block absolute inset-y-0 right-0 w-[34rem] bg-gradient-to-r from-slate-950/0 via-slate-950/80 to-slate-950 pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center justify-center md:items-end h-full px-4 sm:px-6 md:pr-10 lg:pr-16">
        <div className="absolute top-4 left-4 sm:top-6 sm:left-6">
          <BrandLockup compact />
        </div>
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 rounded-lg border border-amber-200/25 bg-amber-300/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-100/90 backdrop-blur-sm">
          {BETA_LABEL}
        </div>

        <div className="w-full max-w-sm sm:max-w-md">
          <div className="text-center md:text-left mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-3 mb-3 sm:mb-4">
              <BrandMark className="h-12 w-12 rounded-xl p-1 sm:h-14 sm:w-14" />
              <div className="hidden h-px w-16 bg-gradient-to-r from-cyan-300/60 to-transparent md:block" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-2">
              {BRAND_NAME}
            </h1>
            <p className="text-sm sm:text-base text-slate-400 max-w-xs mx-auto md:mx-0">
              Grow a planet from bare rock into a living civilisation — then lead it into space against your class.
            </p>
          </div>

          <div
            className={`grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5 sm:mb-6 transition-all duration-700 ${
              showFeatures ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {LANDING_FEATURES.map((f, i) => (
              <div
                key={f.label}
                data-landing-feature
                className="flex min-h-12 items-center gap-2 rounded-lg border border-slate-700/60 bg-slate-900/65 px-2.5 py-1.5 shadow-lg shadow-black/10 backdrop-blur-sm"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${f.accent}`}>
                  <f.icon className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 leading-tight">
                  <span className="block truncate text-[11px] font-semibold text-slate-100">{f.label}</span>
                  <span className="block truncate text-[10px] uppercase tracking-[0.12em] text-slate-500">{f.detail}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-4 sm:p-6 shadow-2xl shadow-black/30">
            <div className="mb-4 flex items-center justify-between gap-3">
              <BrandLockup />
              <div className="h-px flex-1 bg-gradient-to-r from-slate-700/70 to-transparent" />
            </div>
            <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-700/60 bg-slate-950/45 p-1">
                <button
                  type="button"
                  onClick={() => {
                    setEntryRole('student');
                    setError(null);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    entryRole === 'student' ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  Student
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEntryRole('teacher');
                    setError(null);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    entryRole === 'teacher' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  Teacher
                </button>
              </div>

              {entryRole === 'student' ? (
                <>
                  <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-700/60 bg-slate-950/45 p-1">
                    <button
                      type="button"
                      onClick={() => setStudentMode('join')}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        studentMode === 'join' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Join Class
                    </button>
                    <button
                      type="button"
                      onClick={() => setStudentMode('login')}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        studentMode === 'login' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Return to Planet
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-700/60 bg-slate-950/45 p-1">
                    <button
                      type="button"
                      onClick={() => {
                        setStudentUniverseMode('public');
                        setError(null);
                      }}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        studentUniverseMode === 'public' ? 'bg-cyan-700 text-white' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Public Universe
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStudentUniverseMode('teacherCode');
                        setError(null);
                      }}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        studentUniverseMode === 'teacherCode' ? 'bg-cyan-700 text-white' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Teacher Code
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Student username</label>
                    <input
                      type="text"
                      autoComplete="username"
                      placeholder={studentMode === 'join' ? 'Choose a username' : 'Enter your username'}
                      value={studentUsername}
                      onChange={(e) => setStudentUsername(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleStudentAuth()}
                      className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                    />
                  </div>
                  {studentUniverseMode === 'teacherCode' && (
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Universe code</label>
                      <div className="relative">
                        <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                        <input
                          type="text"
                          placeholder="Teacher code"
                          value={studentUniverseCode}
                          onChange={(e) => setStudentUniverseCode(e.target.value.toUpperCase())}
                          onKeyDown={(e) => e.key === 'Enter' && handleStudentAuth()}
                          className="w-full rounded-xl border border-slate-700 bg-slate-800 py-2.5 pl-9 pr-3.5 text-sm uppercase tracking-[0.18em] text-white placeholder:normal-case placeholder:tracking-normal placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Password</label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="password"
                        autoComplete={studentMode === 'join' ? 'new-password' : 'current-password'}
                        placeholder={studentMode === 'join' ? 'Create a password (6+ characters)' : 'Enter your password'}
                        value={studentPassword}
                        onChange={(e) => setStudentPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleStudentAuth()}
                        className="w-full rounded-xl border border-slate-700 bg-slate-800 py-2.5 pl-9 pr-3.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                      />
                    </div>
                    <div className="mt-1 text-[10px] text-slate-500">
                      {studentPassword.length > 0 && studentPassword.length < 6
                        ? 'Password needs at least 6 characters.'
                        : studentUniverseMode === 'public'
                        ? 'Your password returns you to the same public planet.'
                        : 'Universe code selects your class. Your password returns you to the same planet.'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-700/60 bg-slate-950/45 p-1">
                    <button
                      type="button"
                      onClick={() => setTeacherMode('login')}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        teacherMode === 'login' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Teacher Login
                    </button>
                    <button
                      type="button"
                      onClick={() => setTeacherMode('register')}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        teacherMode === 'register' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      Register
                    </button>
                  </div>
                  {teacherMode === 'register' && (
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Teacher name</label>
                      <input
                        type="text"
                        autoComplete="name"
                        placeholder="Your name"
                        value={teacherName}
                        onChange={(e) => setTeacherName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleTeacherAuth()}
                        className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Teacher email</label>
                    <input
                      type="email"
                      autoComplete="email"
                      placeholder="teacher@school.org"
                      value={teacherEmail}
                      onChange={(e) => setTeacherEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleTeacherAuth()}
                      className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1.5">Password</label>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        type="password"
                        autoComplete={teacherMode === 'register' ? 'new-password' : 'current-password'}
                        placeholder={teacherMode === 'register' ? 'Create a password (6+ characters)' : 'Enter your password'}
                        value={teacherPassword}
                        onChange={(e) => setTeacherPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleTeacherAuth()}
                        className="w-full rounded-xl border border-slate-700 bg-slate-800 py-2.5 pl-9 pr-3.5 text-sm text-white placeholder-slate-500 focus:border-purple-500/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      />
                    </div>
                    <div className="mt-1 text-[10px] text-slate-500">
                      {teacherPassword.length > 0 && teacherPassword.length < 6
                        ? 'Password needs at least 6 characters.'
                        : 'Teachers create class universes and share join codes from the dashboard.'}
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="px-3 py-2 bg-red-900/40 border border-red-700/40 rounded-lg text-red-300 text-xs sm:text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
                <button
                  onClick={entryRole === 'student' ? handleStudentAuth : handleTeacherAuth}
                  disabled={
                    loading ||
                    (entryRole === 'student' && (!studentUsername.trim() || (studentUniverseMode === 'teacherCode' && !studentUniverseCode.trim()) || studentPassword.length < 6)) ||
                    (entryRole === 'teacher' && (!teacherEmail.trim() || teacherPassword.length < 6))
                  }
                  className={`group flex items-center justify-center gap-1.5 px-4 py-2.5 sm:py-3 disabled:bg-slate-700 disabled:text-slate-500 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] ${
                    entryRole === 'student' ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-purple-600 hover:bg-purple-500'
                  }`}
                >
                  {loading ? (
                    <span className="animate-pulse">Working...</span>
                  ) : (
                    <>
                      {entryRole === 'student'
                        ? (studentMode === 'join'
                          ? (studentUniverseMode === 'public' ? 'Join Public Universe' : 'Join Class')
                          : 'Return to Planet')
                        : (teacherMode === 'register' ? 'Create Teacher Account' : 'Teacher Login')}
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {isDev && planets.length > 0 && (
            <div className="mt-4 sm:mt-5">
              <button
                onClick={() => setShowContinue(!showContinue)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50 rounded-xl text-sm text-slate-300 transition-colors"
              >
                <Play className="w-4 h-4 text-green-400" />
                {showContinue ? 'Hide saved planets' : `Continue playing (${planets.length} planet${planets.length !== 1 ? 's' : ''})`}
              </button>

              {showContinue && (
                <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-xl p-3">
                  {planets.map((p) => (
                    <button
                      key={p.planet_id}
                      onClick={() => handleContinue(p.owner_user_id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-800/60 hover:bg-slate-700/60 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
                        <Orbit className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">{p.name}</div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          <span className="flex items-center gap-0.5">
                            <User className="w-2.5 h-2.5" />
                            {p.user?.name || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" />
                            Stage {p.stage}
                          </span>
                          <span>{p._count?.upgrade_instances || 0} upgrades</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {isDev && (
            <details className="mt-3 rounded-xl border border-slate-700/50 bg-slate-900/70 p-3 text-xs text-slate-400">
              <summary className="cursor-pointer font-semibold text-slate-300">Dev shortcuts</summary>
              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  placeholder="Preview teacher name"
                  value={devTeacherName}
                  onChange={(e) => setDevTeacherName(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                />
                <button
                  onClick={handleDevTeacherCreate}
                  disabled={loading || !devTeacherName.trim()}
                  className="rounded-lg bg-purple-600 px-3 py-2 font-semibold text-white disabled:bg-slate-700 disabled:text-slate-500"
                >
                  Seed Teacher
                </button>
              </div>
            </details>
          )}

          <p className="text-center text-[10px] sm:text-xs text-slate-500 mt-4 sm:mt-5">
            Students can join a public universe or use a teacher code. Teachers use email login and manage codes in the dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}
